import React, { useState, useEffect, useCallback } from 'react'
import { client, hostedFields } from 'braintree-web'
import { useParams } from 'react-router-dom'
import CreditCardForm from './CreditCardForm'
import { PaymentNonceDto } from '../@types/PaymentNonceDto'
import { getBaseURL } from '../GlobalComponents/URLs'
import { getCookie } from '../GlobalComponents/GlobalFunctions'
import moment from 'moment'

interface props {
    requestType: string
}
export default function CreditCard({ requestType }: props) {
    const [hostedFieldsValues, setHostedFields] = useState<{ hostedFieldsValues: any }>()
    const [submitting, setSubmitting] = useState(false)
    const [readyToSubmit, setReadyToSubmit] = useState(false)
    const urlParams = useParams()
    const [creditCardType, setCreditCardType] = useState('')
    const [error, setError] = useState('')

    const creditCardServiceUrl = `${getBaseURL()}webapics/api/v1/paymentmethod`

    function submit(event: any, hostedFieldsInstance: any) {
        event.preventDefault()
        hostedFieldsInstance.tokenize(function (tokenizeErr: any, payload: any) {
            if (tokenizeErr) {
                setError('We had an error sending the information')
                return
            }

            const paymentNonceDto: PaymentNonceDto = {
                Nonce: payload.nonce,
                ContactId: requestType === 'add' ? +urlParams.contactId! : 0,
                CustomerId: urlParams.customerId!,
                Jtoken: '',
                Token: requestType === 'update' ? urlParams.token! : '',
                QuoteNumber: urlParams.quoteNumber!,
            }

            SendTokenInfo(paymentNonceDto)
        })
    }

    function checkCardType(name: string) {
        switch (name) {
        case 'visa':
            return true
        case 'master-card':
            return true
        case 'maestro':
            return false
        case 'american-express':
            return true
        case 'discover':
            return true
        case 'jcb':
            return false
        default:
            return false
        }
    }
    async function SendTokenInfo(paymentNonceDto: PaymentNonceDto) {
        let company: string = urlParams.database!
        setReadyToSubmit(false)
        setSubmitting(true)
        setError('') //#6902 don't show Sending Information
        await fetch(creditCardServiceUrl, {
            method: requestType === 'add' ? 'POST' : 'PUT',
            mode: 'cors',
            credentials: 'include',
            headers: {
                'content-type': 'application/json',
                company: company,
            },
            body: JSON.stringify(paymentNonceDto, [
                'Nonce',
                'ContactId',
                'CustomerId',
                'Jtoken',
                'Token',
                'QuoteNumber',
            ]),
        })
            .then((response) => {
                if (response.ok) {
                    let returnPage = ''
                    if (
                        urlParams.returnPage != null &&
                        urlParams.returnPage === 'PaymentMethods.aspx'
                    ) {
                        if (urlParams.quoteNumber !== '0') {
                            returnPage = `${getBaseURL()}PaymentMethods.aspx?Quote=${
                                urlParams.quoteNumber
                            }`
                        } else {
                            returnPage = `${getBaseURL()}PaymentMethods.aspx?New=1`
                        }
                    } else {
                        if (urlParams.returnPage !== null) {
                            returnPage = `${getBaseURL() + urlParams.returnPage}`
                        } else {
                            returnPage = `${getBaseURL()}PaymentMethods.aspx`
                        }
                    }
                    window.location.href = returnPage
                } else {
                    setSubmitting(false)
                    setError('We had an issue calling the server')
                }
            })
            .catch((error) => {
                setSubmitting(false)
                setError('We had an issue with the server')
            })
    }

    const validityChange = useCallback((event: any) => {
        const formValid = Object.keys(event.fields).every(function (key) {
            return event.fields[key].isValid
        })
        if (formValid && !checkCardType(event.cards[0]?.type)) {
            setError('Invalid Credit Card Type')
            setReadyToSubmit(false)
        } else if (formValid && checkCardType(event.cards[0]?.type)) {
            setError('')
            setReadyToSubmit(formValid)
        }
    }, [])

    function handleCancel() {
        window.location.href =
            urlParams.cancelPage != null
                ? `${getBaseURL() + urlParams.cancelPage}`
                : `${getBaseURL()}PaymentMethods.aspx`
    }

    useEffect(() => {
        if (typeof urlParams.customerId !== 'undefined') {
            let customerToken = getCookie('brainTree')
            client.create(
                {
                    authorization: customerToken,
                },
                function (clientErr, clientInstance) {
                    if (clientErr) {
                        setError('Failed to authorize, please try again later')
                        return
                    }

                    hostedFields.create(
                        {
                            client: clientInstance,
                            styles: {
                                input: {
                                    'font-size': '16px',
                                    transition: 'color 0.1s',
                                    'line-height': '3',
                                },
                                'input.invalid': {
                                    color: 'red',
                                },
                                '::-webkit-input-placeholder': {
                                    color: 'rgba(0,0,0,0.6)',
                                },
                                ':-moz-placeholder': {
                                    color: 'rgba(0,0,0,0.6)',
                                },
                                '::-moz-placeholder': {
                                    color: 'rgba(0,0,0,0.6)',
                                },
                                ':-ms-input-placeholder': {
                                    color: 'rgba(0,0,0,0.6)',
                                },
                                // prevent IE 11 and Edge from
                                // displaying the clear button
                                // over the card brand icon
                                'input::-ms-clear': {
                                    opacity: '0',
                                },
                            },
                            fields: {
                                number: {
                                    container: '#card-number',
                                    placeholder: '4111 1111 1111 1111',
                                },
                                cvv: {
                                    container: '#cvv',
                                    placeholder: '123',
                                },
                                expirationDate: {
                                    container: '#expiration-date',
                                    placeholder: moment().add(2, 'years').format('MM/YY'),
                                },
                                cardholderName: {
                                    container: '#cardholdername',
                                    placeholder: 'John Smith',
                                },
                                postalCode: {
                                    container: '#postalCode',
                                    placeholder: '98020',
                                },
                            },
                        },
                        function (hostedFieldsErr, hostedFieldsInstance: any) {
                            if (hostedFieldsErr) {
                                setError(
                                    'Network error, check your if browser accepts cookies and refresh the page'
                                )
                                return
                            }
                            hostedFieldsInstance.on('validityChange', (e: any) => validityChange(e))
                            hostedFieldsInstance.on('cardTypeChange', (e: any) =>
                                setCreditCardType(e.cards[0]?.type)
                            )
                            setHostedFields(hostedFieldsInstance)
                        }
                    )
                }
            )
        }
    }, [urlParams, validityChange])

    return (
        <CreditCardForm
            submmiting={submitting}
            readyToSubmit={readyToSubmit}
            handleClick={(e) => submit(e, hostedFieldsValues)}
            creditCardType={creditCardType}
            error={error}
            handleCancel={handleCancel}
        />
    )
}
